import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Gem, Layers, Layout, Sun } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { useSEO } from '../../hooks/useSEO';
// Images
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/vBzCByw2N7rP6MS6jLeNiZ/_DSC8493.webp';
const DETAIL_IMAGE_1 = 'https://cdn.magicpatterns.com/uploads/3xL1jkjmSyiggyKFQmbzxH/_DSC8261.webp';
const DETAIL_IMAGE_2 = 'https://cdn.magicpatterns.com/uploads/pKPzBNwMUWtZUzQjDCs91d/6842e88f2f523e985c5fa1a4__DSC8349-2.jpg';
export function PremiumInteriorsPage() {
  useSEO({
    title: 'Premium Interiors — Yacht-Inspired Campervan Design',
    description: 'Step inside a Robeta. Yacht-inspired interiors with HPL surfaces, hand-stitched upholstery, ambient lighting, and ergonomic design built to last.'
  });
  return <main className="bg-white min-h-screen" data-id="element-1014">
      {/* 1. FULL-SCREEN HERO */}
      <section className="relative min-h-screen w-full flex flex-col justify-between overflow-hidden" data-id="element-1015">
        <div className="absolute inset-0 z-0" data-id="element-1016">
          <img src={HERO_IMAGE} alt="Premium Interior Hero" className="w-full h-full object-cover" data-id="element-1017" />
          <div className="absolute inset-0 bg-black/40" data-id="element-1018" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" data-id="element-1019" />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10 pt-32" data-id="element-1020">
          <Breadcrumbs items={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Robeta Difference',
          href: '/robeta-difference'
        }, {
          label: 'Premium Interiors'
        }]} variant="light" data-id="element-1021" />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10 pb-24 md:pb-32" data-id="element-1022">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="max-w-4xl" data-id="element-1023">
            <span className="text-robeta-copper text-sm font-bold uppercase tracking-wide-editorial block mb-6" data-id="element-1024">
              Yacht-Inspired Design
            </span>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white uppercase tracking-editorial leading-[0.9] mb-8" data-id="element-1025">
              Premium
              <br data-id="element-1026" />
              Interiors
            </h1>
            <p className="text-xl md:text-2xl font-light text-white/80 max-w-2xl leading-relaxed" data-id="element-1027">
              Where form meets function in perfect harmony.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. INTRO SECTION */}
      <section className="py-24 md:py-32 bg-white" data-id="element-1028">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1029">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1030">
            <h2 className="text-3xl md:text-5xl font-light text-robeta-black leading-tight mb-8" data-id="element-1031">
              "We don't build camper vans. We build{' '}
              <span className="italic font-serif text-robeta-copper" data-id="element-1032">
                mobile residences
              </span>
              ."
            </h2>
            <p className="text-lg text-robeta-black/60 font-light leading-relaxed max-w-2xl mx-auto" data-id="element-1033">
              Step inside a Robeta, and you immediately feel the difference.
              There are no sharp edges, no cheap plastics, and no compromises.
              Inspired by the world of luxury yachting, our interiors feature
              flowing lines, premium materials, and an ergonomic layout that
              makes 6 meters feel like 60.
            </p>
          </div>
        </div>
      </section>

      {/* 3. DEEP DIVE 1: MATERIAL EXCELLENCE */}
      <section className="py-20 md:py-32 bg-robeta-mist/10" data-id="element-1034">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1035">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1036">
            <div className="order-2 lg:order-1" data-id="element-1037">
              <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-4" data-id="element-1038">
                Tactile Luxury
              </span>
              <h3 className="text-3xl md:text-4xl font-bold text-robeta-black uppercase tracking-editorial mb-6" data-id="element-1039">
                Material Excellence
              </h3>
              <div className="space-y-6 text-robeta-black/70 font-light leading-relaxed" data-id="element-1040">
                <p data-id="element-1041">
                  Touch any surface in a Robeta, and you'll understand our
                  obsession with quality. We use{' '}
                  <strong data-id="element-1042">High Pressure Laminate (HPL)</strong> for all
                  cabinetry surfaces. Unlike the paper foils used by others, HPL
                  is incredibly resistant to scratches, heat, and moisture.
                </p>
                <p data-id="element-1043">
                  Our upholstery is hand-stitched from premium automotive-grade
                  leathers and fabrics that are durable yet soft to the touch.
                  Countertops are solid and substantial. Even the ceiling is
                  lined with soft-touch materials to dampen sound and create a
                  cozy atmosphere.
                </p>
                <p data-id="element-1044">
                  Every material is chosen not just for how it looks in the
                  showroom, but for how it will perform after 100,000 kilometers
                  of adventure.
                </p>
              </div>
            </div>
            <div className="order-1 lg:order-2 relative aspect-[4/3] overflow-hidden" data-id="element-1045">
              <img src={DETAIL_IMAGE_1} alt="Luxury Interior View" className="absolute inset-0 w-full h-full object-cover" data-id="element-1046" />
            </div>
          </div>
        </div>
      </section>

      {/* 4. DEEP DIVE 2: ERGONOMIC FLOW */}
      <section className="py-20 md:py-32 bg-white" data-id="element-1047">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1048">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1049">
            <div className="relative aspect-[4/3] overflow-hidden" data-id="element-1050">
              <img src={DETAIL_IMAGE_2} alt="Ergonomic Cabinetry" className="absolute inset-0 w-full h-full object-cover" data-id="element-1051" />
            </div>
            <div data-id="element-1052">
              <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-4" data-id="element-1053">
                Living Design
              </span>
              <h3 className="text-3xl md:text-4xl font-bold text-robeta-black uppercase tracking-editorial mb-6" data-id="element-1054">
                Ergonomic Flow
              </h3>
              <div className="space-y-6 text-robeta-black/70 font-light leading-relaxed" data-id="element-1055">
                <p data-id="element-1056">
                  Living in a compact space requires intelligent design. Our
                  "Air" furniture features curved edges that not only look
                  beautiful but improve traffic flow through the van. You won't
                  catch your hip on a sharp corner in a Robeta.
                </p>
                <p data-id="element-1057">
                  We place drawers where you naturally reach for them. Lighting
                  switches are intuitive. The bathroom door is designed to
                  maximize space whether open or closed.
                </p>
                <p data-id="element-1058">
                  It's a philosophy of "invisible design"—when everything works
                  exactly as it should, you stop noticing the design and simply
                  enjoy the living.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. KEY BENEFITS GRID */}
      <section className="py-24 bg-robeta-black text-white" data-id="element-1059">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1060">
          <div className="text-center mb-16" data-id="element-1061">
            <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-editorial mb-4" data-id="element-1062">
              Details Matter
            </h2>
            <div className="w-24 h-1 bg-robeta-copper mx-auto" data-id="element-1063" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1064">
            {[{
            icon: Layers,
            title: 'HPL Surfaces',
            desc: 'Scratch, heat, and moisture resistant surfaces that stay beautiful for years.'
          }, {
            icon: Layout,
            title: 'Soft-Close Tech',
            desc: 'Premium hardware on all drawers and cabinets for silent, smooth operation.'
          }, {
            icon: Sun,
            title: 'Ambient Lighting',
            desc: 'Dimmable, multi-zone LED lighting to set the perfect mood for any moment.'
          }, {
            icon: Gem,
            title: 'Hand-Stitched',
            desc: 'Upholstery crafted by skilled artisans for perfect fit and finish.'
          }].map((item, i) => <div key={i} className="bg-white/5 p-8 border border-white/10 hover:border-robeta-copper/50 transition-colors" data-id="element-1065">
                <item.icon className="w-10 h-10 text-robeta-copper mb-6" data-id="element-1066" />
                <h3 className="text-xl font-bold uppercase tracking-wide mb-4" data-id="element-1067">
                  {item.title}
                </h3>
                <p className="text-white/60 font-light leading-relaxed text-sm" data-id="element-1068">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. NEXT PILLAR NAV */}
      <section className="border-t border-robeta-black/10 bg-white" data-id="element-1069">
        <div className="container mx-auto px-6 md:px-12 py-16 md:py-24" data-id="element-1070">
          <div className="flex flex-col items-center text-center" data-id="element-1071">
            <span className="text-robeta-black/40 text-xs font-bold uppercase tracking-widest mb-4" data-id="element-1072">
              Next Pillar
            </span>
            <Link to="/robeta-difference/customization" className="group flex flex-col items-center" data-id="element-1073">
              <h2 className="text-4xl md:text-6xl font-bold text-robeta-black uppercase tracking-editorial mb-6 group-hover:text-robeta-copper transition-colors" data-id="element-1074">
                16,000 Customization Options
              </h2>
              <div className="flex items-center text-sm font-bold uppercase tracking-widest text-robeta-black group-hover:text-robeta-copper transition-colors" data-id="element-1075">
                Make It Yours <ArrowRight className="ml-2 w-4 h-4" data-id="element-1076" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* 7. CTA SECTION */}
      <section className="py-28 md:py-36 bg-robeta-mist/20 text-center" data-id="element-1077">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1078">
          <h2 className="text-4xl md:text-5xl font-bold text-robeta-black uppercase tracking-editorial mb-8" data-id="element-1079">
            Step Inside Luxury
          </h2>
          <p className="text-lg text-robeta-black/60 font-light max-w-2xl mx-auto mb-12" data-id="element-1080">
            Visit a dealer to touch, feel, and experience the Robeta interior.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-6" data-id="element-1081">
            <Link to="/dealer-network" className="px-10 py-4 bg-robeta-black text-white font-bold uppercase tracking-widest hover:bg-robeta-copper transition-colors min-w-[200px]" data-id="element-1082">
              Find a Dealer
            </Link>
            <Link to="/models" className="px-10 py-4 border border-robeta-black text-robeta-black font-bold uppercase tracking-widest hover:bg-white transition-colors min-w-[200px]" data-id="element-1083">
              View Models
            </Link>
          </div>
        </div>
      </section>
    </main>;
}